import firebase from '../config';

/**
 * Exam Cache Service
 * Provides intelligent caching for exam data to reduce Firebase downloads
 * Implements multiple cache layers for optimal performance
 */
class ExamCacheService {
  // Cache configuration
  static CACHE_CONFIG = {
    EXAM_LIST: {
      key: 'examListCache',
      duration: 60 * 60 * 1000, // 1 hour
      maxSize: 50 // Maximum number of exams to cache
    },
    EXAM_DETAILS: {
      key: 'examDetailsCache',
      duration: 4 * 60 * 60 * 1000, // 4 hours (exam duration)
      maxSize: 100 // Maximum number of exam details to cache
    },
    STUDENT_EXAM: {
      key: 'studentExamCache',
      duration: 4 * 60 * 60 * 1000, // 4 hours
      maxSize: 20 // Maximum number of student exam sessions
    },
    SHARED_EXAM: {
      key: 'sharedExamCache',
      duration: 24 * 60 * 60 * 1000, // 24 hours
      maxSize: 50 // Maximum number of shared exam caches
    }
  };

  /**
   * Check if cache is valid
   */
  static isCacheValid(cacheData, cacheType = 'EXAM_LIST') {
    if (!cacheData) return false;
    
    const config = this.CACHE_CONFIG[cacheType];
    if (!config) return false;
    
    const now = Date.now();
    const cacheAge = now - (cacheData.timestamp || 0);
    
    return cacheAge < config.duration;
  }

  /**
   * Get cache size in bytes
   */
  static getCacheSize(cacheData) {
    if (!cacheData) return 0;
    return new Blob([JSON.stringify(cacheData)]).size;
  }

  /**
   * Clean up expired cache entries
   */
  static cleanupExpiredCache(cacheType = 'EXAM_LIST') {
    try {
      const config = this.CACHE_CONFIG[cacheType];
      if (!config) return;
      
      const cacheKey = config.key;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const now = Date.now();
        
        // Remove expired entries
        if (parsed.data && Array.isArray(parsed.data)) {
          parsed.data = parsed.data.filter(item => {
            if (!item.timestamp) return false;
            return (now - item.timestamp) < config.duration;
          });
          
          // Update timestamp
          parsed.timestamp = now;
          
          // Save cleaned cache
          localStorage.setItem(cacheKey, JSON.stringify(parsed));
        }
      }
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  /**
   * Set exam list cache
   */
  static setExamListCache(examList) {
    try {
      const config = this.CACHE_CONFIG.EXAM_LIST;
      const cacheData = {
        data: examList,
        timestamp: Date.now(),
        count: examList.length,
        size: this.getCacheSize(examList)
      };
      
      localStorage.setItem(config.key, JSON.stringify(cacheData));
      
      // Clean up old cache entries
      this.cleanupExpiredCache('EXAM_LIST');
      
      console.log(`💾 Exam list cached: ${examList.length} exams`);
      return true;
    } catch (error) {
      console.warn('Failed to cache exam list:', error);
      return false;
    }
  }

  /**
   * Get exam list cache
   */
  static getExamListCache() {
    try {
      const config = this.CACHE_CONFIG.EXAM_LIST;
      const cachedData = localStorage.getItem(config.key);
      
      if (!cachedData) return null;
      
      const parsed = JSON.parse(cachedData);
      
      if (this.isCacheValid(parsed, 'EXAM_LIST')) {
        console.log(`📖 Exam list loaded from cache: ${parsed.count} exams`);
        return parsed.data;
      }
      
      // Cache expired, remove it
      localStorage.removeItem(config.key);
      return null;
      
    } catch (error) {
      console.warn('Failed to get exam list cache:', error);
      localStorage.removeItem(this.CACHE_CONFIG.EXAM_LIST.key);
      return null;
    }
  }

  /**
   * Set exam details cache
   */
  static setExamCache(examCode, examData) {
    try {
      const config = this.CACHE_CONFIG.EXAM_DETAILS;
      const cacheKey = `${config.key}_${examCode}`;
      
      const cacheData = {
        data: examData,
        timestamp: Date.now(),
        examCode,
        size: this.getCacheSize(examData)
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      
      // Clean up old cache entries
      this.cleanupExpiredCache('EXAM_DETAILS');
      
      console.log(`💾 Exam cached: ${examCode}`);
      return true;
    } catch (error) {
      console.warn(`Failed to cache exam ${examCode}:`, error);
      return false;
    }
  }

  /**
   * Get exam details cache
   */
  static getExamCache(examCode) {
    try {
      const config = this.CACHE_CONFIG.EXAM_DETAILS;
      const cacheKey = `${config.key}_${examCode}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) return null;
      
      const parsed = JSON.parse(cachedData);
      
      if (this.isCacheValid(parsed, 'EXAM_DETAILS')) {
        console.log(`📖 Exam loaded from cache: ${examCode}`);
        return parsed.data;
      }
      
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      return null;
      
    } catch (error) {
      console.warn(`Failed to get exam cache for ${examCode}:`, error);
      localStorage.removeItem(`${this.CACHE_CONFIG.EXAM_DETAILS.key}_${examCode}`);
      return null;
    }
  }

  /**
   * Set student exam cache (for students taking exams)
   */
  static setStudentExamCache(examCode, studentEmail, examData) {
    try {
      const config = this.CACHE_CONFIG.STUDENT_EXAM;
      const cacheKey = `${config.key}_${examCode}_${studentEmail}`;
      
      const cacheData = {
        data: examData,
        timestamp: Date.now(),
        examCode,
        studentEmail,
        size: this.getCacheSize(examData)
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      
      // Clean up old cache entries
      this.cleanupExpiredCache('STUDENT_EXAM');
      
      console.log(`💾 Student exam cached: ${examCode} for ${studentEmail}`);
      return true;
    } catch (error) {
      console.warn(`Failed to cache student exam ${examCode}:`, error);
      return false;
    }
  }

  /**
   * Get student exam cache
   */
  static getStudentExamCache(examCode, studentEmail) {
    try {
      const config = this.CACHE_CONFIG.STUDENT_EXAM;
      const cacheKey = `${config.key}_${examCode}_${studentEmail}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) return null;
      
      const parsed = JSON.parse(cachedData);
      
      if (this.isCacheValid(parsed, 'STUDENT_EXAM')) {
        console.log(`📖 Student exam loaded from cache: ${examCode} for ${studentEmail}`);
        return parsed.data;
      }
      
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      return null;
      
    } catch (error) {
      console.warn(`Failed to get student exam cache for ${examCode}:`, error);
      localStorage.removeItem(`${this.CACHE_CONFIG.STUDENT_EXAM.key}_${examCode}_${studentEmail}`);
      return null;
    }
  }

  /**
   * Set shared exam cache (for multiple students taking same exam)
   */
  static setSharedExamCache(examCode, examData) {
    try {
      const config = this.CACHE_CONFIG.SHARED_EXAM;
      const cacheKey = `${config.key}_${examCode}`;
      
      const cacheData = {
        data: examData,
        timestamp: Date.now(),
        examCode,
        size: this.getCacheSize(examData),
        shared: true
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      
      // Clean up old cache entries
      this.cleanupExpiredCache('SHARED_EXAM');
      
      console.log(`💾 Shared exam cached: ${examCode}`);
      return true;
    } catch (error) {
      console.warn(`Failed to cache shared exam ${examCode}:`, error);
      return false;
    }
  }

  /**
   * Get shared exam cache
   */
  static getSharedExamCache(examCode) {
    try {
      const config = this.CACHE_CONFIG.SHARED_EXAM;
      const cacheKey = `${config.key}_${examCode}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) return null;
      
      const parsed = JSON.parse(cachedData);
      
      if (this.isCacheValid(parsed, 'SHARED_EXAM')) {
        console.log(`📖 Shared exam loaded from cache: ${examCode}`);
        return parsed.data;
      }
      
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      return null;
      
    } catch (error) {
      console.warn(`Failed to get shared exam cache for ${examCode}:`, error);
      localStorage.removeItem(`${this.CACHE_CONFIG.SHARED_EXAM.key}_${examCode}`);
      return null;
    }
  }

  /**
   * Clear specific exam cache
   */
  static clearExamCache(examCode) {
    try {
      const config = this.CACHE_CONFIG.EXAM_DETAILS;
      const cacheKey = `${config.key}_${examCode}`;
      localStorage.removeItem(cacheKey);
      
      // Also clear shared cache
      const sharedConfig = this.CACHE_CONFIG.SHARED_EXAM;
      const sharedCacheKey = `${sharedConfig.key}_${examCode}`;
      localStorage.removeItem(sharedCacheKey);
      
      console.log(`🗑️ Exam cache cleared: ${examCode}`);
      return true;
    } catch (error) {
      console.warn(`Failed to clear exam cache for ${examCode}:`, error);
      return false;
    }
  }

  /**
   * Clear exam list cache
   */
  static clearExamListCache() {
    try {
      const config = this.CACHE_CONFIG.EXAM_LIST;
      localStorage.removeItem(config.key);
      console.log('🗑️ Exam list cache cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear exam list cache:', error);
      return false;
    }
  }

  /**
   * Clear all caches
   */
  static clearAllCaches() {
    try {
      Object.values(this.CACHE_CONFIG).forEach(config => {
        if (config.key === 'examListCache') {
          localStorage.removeItem(config.key);
        } else {
          // For other cache types, remove all related keys
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(config.key)) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        }
      });
      
      console.log('🗑️ All caches cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear all caches:', error);
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  static getCacheStats() {
    try {
      const stats = {
        examList: { size: 0, count: 0, valid: false },
        examDetails: { size: 0, count: 0, valid: 0, expired: 0 },
        studentExams: { size: 0, count: 0, valid: 0, expired: 0 },
        sharedExams: { size: 0, count: 0, valid: 0, expired: 0 },
        totalSize: 0,
        totalCount: 0
      };

      // Exam list cache
      const examListCache = localStorage.getItem(this.CACHE_CONFIG.EXAM_LIST.key);
      if (examListCache) {
        const parsed = JSON.parse(examListCache);
        stats.examList.size = parsed.size || 0;
        stats.examList.count = parsed.count || 0;
        stats.examList.valid = this.isCacheValid(parsed, 'EXAM_LIST');
        stats.totalSize += stats.examList.size;
        stats.totalCount += stats.examList.count;
      }

      // Exam details cache
      const examDetailsKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_CONFIG.EXAM_DETAILS.key + '_')) {
          examDetailsKeys.push(key);
        }
      }

      examDetailsKeys.forEach(key => {
        try {
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            const size = parsed.size || 0;
            const isValid = this.isCacheValid(parsed, 'EXAM_DETAILS');
            
            stats.examDetails.size += size;
            stats.examDetails.count++;
            stats.totalSize += size;
            stats.totalCount++;
            
            if (isValid) {
              stats.examDetails.valid++;
            } else {
              stats.examDetails.expired++;
            }
          }
        } catch (error) {
          // Skip invalid cache entries
        }
      });

      // Student exam cache
      const studentExamKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_CONFIG.STUDENT_EXAM.key + '_')) {
          studentExamKeys.push(key);
        }
      }

      studentExamKeys.forEach(key => {
        try {
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            const size = parsed.size || 0;
            const isValid = this.isCacheValid(parsed, 'STUDENT_EXAM');
            
            stats.studentExams.size += size;
            stats.studentExams.count++;
            stats.totalSize += size;
            stats.totalCount++;
            
            if (isValid) {
              stats.studentExams.valid++;
            } else {
              stats.studentExams.expired++;
            }
          }
        } catch (error) {
          // Skip invalid cache entries
        }
      });

      // Shared exam cache
      const sharedExamKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_CONFIG.SHARED_EXAM.key + '_')) {
          sharedExamKeys.push(key);
        }
      }

      sharedExamKeys.forEach(key => {
        try {
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            const size = parsed.size || 0;
            const isValid = this.isCacheValid(parsed, 'SHARED_EXAM');
            
            stats.sharedExams.size += size;
            stats.sharedExams.count++;
            stats.totalSize += size;
            stats.totalCount++;
            
            if (isValid) {
              stats.sharedExams.valid++;
            } else {
              stats.sharedExams.expired++;
            }
          }
        } catch (error) {
          // Skip invalid cache entries
        }
      });

      // Format sizes
      stats.totalSizeFormatted = this.formatBytes(stats.totalSize);
      stats.examList.sizeFormatted = this.formatBytes(stats.examList.size);
      stats.examDetails.sizeFormatted = this.formatBytes(stats.examDetails.size);
      stats.studentExams.sizeFormatted = this.formatBytes(stats.studentExams.size);
      stats.sharedExams.sizeFormatted = this.formatBytes(stats.sharedExams.size);

      return stats;
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
      return null;
    }
  }

  /**
   * Format bytes to human readable format
   */
  static formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Optimize cache storage (remove expired entries, limit size)
   */
  static optimizeCache() {
    try {
      console.log('🔧 Optimizing cache storage...');
      
      let removedCount = 0;
      let totalSizeBefore = 0;
      let totalSizeAfter = 0;
      
      // Get current stats
      const statsBefore = this.getCacheStats();
      if (statsBefore) {
        totalSizeBefore = statsBefore.totalSize;
      }
      
      // Clean up all cache types
      Object.keys(this.CACHE_CONFIG).forEach(cacheType => {
        this.cleanupExpiredCache(cacheType);
      });
      
      // Get stats after cleanup
      const statsAfter = this.getCacheStats();
      if (statsAfter) {
        totalSizeAfter = statsAfter.totalSize;
        removedCount = (statsBefore?.totalCount || 0) - statsAfter.totalCount;
      }
      
      console.log(`✅ Cache optimization complete: Removed ${removedCount} expired entries`);
      console.log(`   Size reduction: ${this.formatBytes(totalSizeBefore - totalSizeAfter)}`);
      
      return { removedCount, sizeReduction: totalSizeBefore - totalSizeAfter };
    } catch (error) {
      console.warn('Cache optimization failed:', error);
      return null;
    }
  }
}

export default ExamCacheService; 